/**
 * Credit Service
 * Frontend service for credit management operations
 */

import axios from 'axios';
import { currencyFormattingService } from './currency-formatting.service';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

export interface CreditTransaction {
  id: string;
  manufacturerId: string;
  transactionType: 'LOAD' | 'CONSUME' | 'REFUND' | 'ADJUSTMENT';
  amount: number;
  balanceBefore: number;
  balanceAfter: number;
  operationType?: string;
  referenceId?: string;
  description: string;
  adminId?: string;
  metadata?: Record<string, any>;
  createdAt: string;
  admin?: {
    id: string;
    fullName: string;
    email: string;
  };
}

export interface OperationCost {
  id: string;
  operationType: string;
  cost: number;
  currency: string;
  isActive: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  creator?: {
    id: string;
    fullName: string;
    email: string;
  };
}

export interface ManufacturerWithBalance {
  id: string;
  name: string;
  email: string;
  contactEmail: string;
  creditBalance: number;
  status: string;
  isApproved: boolean;
  createdAt: string;
  totalTransactions: number;
  totalBatches: number;
}

export interface CreditValidation {
  hasCredits: boolean;
  required: number;
  available: number;
}

class CreditService {
  private getAuthHeaders() {
    const token = localStorage.getItem('auth_token');
    return {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    };
  }

  // ============================================================================
  // MANUFACTURER OPERATIONS
  // ============================================================================

  /**
   * Get current credit balance
   */
  async getBalance(): Promise<{ success: boolean; balance?: number; error?: string }> {
    try {
      const response = await axios.get(`${API_BASE_URL}/credit-management/balance`, this.getAuthHeaders());
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch balance',
      };
    }
  }

  /**
   * Get transaction history
   */
  async getTransactionHistory(filters?: {
    transactionType?: string;
    operationType?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
  }): Promise<{
    success: boolean;
    transactions?: CreditTransaction[];
    total?: number;
    error?: string;
  }> {
    try {
      const params = new URLSearchParams();
      if (filters?.transactionType) params.append('transactionType', filters.transactionType);
      if (filters?.operationType) params.append('operationType', filters.operationType);
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.offset) params.append('offset', filters.offset.toString());

      const response = await axios.get(
        `${API_BASE_URL}/credit-management/transactions?${params.toString()}`,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch transaction history',
      };
    }
  }

  /**
   * Validate credits for an operation
   */
  async validateCredits(operationType: string): Promise<{
    success: boolean;
    hasCredits?: boolean;
    required?: number;
    available?: number;
    error?: string;
  }> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/credit-management/validate/${operationType}`,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to validate credits',
      };
    }
  }

  /**
   * Get operation costs
   */
  async getOperationCosts(): Promise<{
    success: boolean;
    costs?: OperationCost[];
    error?: string;
  }> {
    try {
      const response = await axios.get(`${API_BASE_URL}/credit-management/operation-costs`, this.getAuthHeaders());
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch operation costs',
      };
    }
  }

  // ============================================================================
  // ADMIN OPERATIONS
  // ============================================================================

  /**
   * Load credits for a manufacturer (Admin only)
   */
  async loadCredits(
    manufacturerId: string,
    amount: number,
    description?: string
  ): Promise<{
    success: boolean;
    transaction?: CreditTransaction;
    error?: string;
  }> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/credit-management/load`,
        { manufacturerId, amount, description },
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to load credits',
      };
    }
  }

  /**
   * Refund credits to a manufacturer (Admin only)
   */
  async refundCredits(
    manufacturerId: string,
    amount: number,
    reason: string
  ): Promise<{
    success: boolean;
    transaction?: CreditTransaction;
    error?: string;
  }> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/credit-management/refund`,
        { manufacturerId, amount, reason },
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to refund credits',
      };
    }
  }

  /**
   * Adjust credits for a manufacturer (Admin only)
   */
  async adjustCredits(
    manufacturerId: string,
    amount: number,
    reason: string
  ): Promise<{
    success: boolean;
    transaction?: CreditTransaction;
    error?: string;
  }> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/credit-management/adjust`,
        { manufacturerId, amount, reason },
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to adjust credits',
      };
    }
  }

  /**
   * Get all manufacturers with balances (Admin only)
   */
  async getAllManufacturersWithBalances(): Promise<{
    success: boolean;
    manufacturers?: ManufacturerWithBalance[];
    error?: string;
  }> {
    try {
      const response = await axios.get(`${API_BASE_URL}/credit-management/manufacturers`, this.getAuthHeaders());
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch manufacturers',
      };
    }
  }

  /**
   * Get transaction history for a specific manufacturer (Admin only)
   */
  async getManufacturerTransactionHistory(
    manufacturerId: string,
    filters?: {
      transactionType?: string;
      operationType?: string;
      startDate?: string;
      endDate?: string;
      limit?: number;
      offset?: number;
    }
  ): Promise<{
    success: boolean;
    transactions?: CreditTransaction[];
    total?: number;
    error?: string;
  }> {
    try {
      const params = new URLSearchParams();
      if (filters?.transactionType) params.append('transactionType', filters.transactionType);
      if (filters?.operationType) params.append('operationType', filters.operationType);
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.offset) params.append('offset', filters.offset.toString());

      const response = await axios.get(
        `${API_BASE_URL}/credit-management/transactions/${manufacturerId}?${params.toString()}`,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch transaction history',
      };
    }
  }

  /**
   * Update operation cost (Admin only)
   */
  async updateOperationCost(
    operationType: string,
    cost: number
  ): Promise<{
    success: boolean;
    operationCost?: OperationCost;
    error?: string;
  }> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/operation-costs`,
        { operationType, cost },
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update operation cost',
      };
    }
  }

  /**
   * Get operation cost history (Admin only)
   */
  async getOperationCostHistory(operationType: string): Promise<{
    success: boolean;
    costs?: OperationCost[];
    operationType?: string;
    error?: string;
  }> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/operation-costs/history/${operationType}`,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch cost history',
      };
    }
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Format amount for display
   * Note: Credit balances are stored as decimal values in the database (e.g., 199.90),
   * NOT in minor units (paise). So we format directly without conversion.
   */
  formatAmount(amount: number, currency: string = 'INR'): string {
    return currencyFormattingService.formatCurrency(amount, currency);
  }

  /**
   * Parse amount from user input (keep in main currency units; backend expects rupees)
   */
  parseAmount(amount: string | number): number {
    const value = typeof amount === 'string' ? parseFloat(amount) : amount;
    return Number.isFinite(value) ? value : 0;
  }

  /**
   * Get transaction type display name
   */
  getTransactionTypeDisplay(type: string): string {
    const types: Record<string, string> = {
      LOAD: 'Credit Loaded',
      CONSUME: 'Credit Consumed',
      REFUND: 'Credit Refunded',
      ADJUSTMENT: 'Credit Adjusted',
    };
    return types[type] || type;
  }

  /**
   * Get operation type display name
   */
  getOperationTypeDisplay(type: string): string {
    const types: Record<string, string> = {
      BATCH_CREATION: 'Batch Creation',
      PRODUCT_ACTIVATION: 'Product Activation',
      QR_SCAN: 'QR Code Scan',
      TRANSFER: 'Product Transfer',
      PACKAGE_ASSIGNMENT: 'Package Assignment',
    };
    return types[type] || type;
  }
}

export const creditService = new CreditService();
